import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { getPropertyTypeString } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.utils";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { EPaymentRepetitionType } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { IPaymentRequest } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import {
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import * as paymentRequestUtils from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import queryString from "query-string";

export const PAYMENTS_ROUTE = "/payments";

export const DIRECT_DEBITS_ROUTE = "/payments/direct-debits";

export { isTrustedMandateRedirectUrl } from "@rentiohq/shared-frontend/dist/utils/mangopay.utils";

export const valuesForPaymentRequest = (
  paymentRequest: IPaymentRequest,
  property?: IProperty,
) => {
  const paymentType = paymentRequestUtils.getTitle(paymentRequest);

  const isRecurring =
    paymentRequest.repetitionType !== EPaymentRepetitionType.Once;

  const optionalPeriod = isRecurring
    ? getLocalizedText(
        "payment.payment_order.first_payment.period.value.basic",
        {
          startDate: formatDate(paymentRequest.startedAt, "d MMMM yyyy"),
          endDate: formatDate(paymentRequest.endedAt, "d MMMM yyyy"),
        },
      ).toLowerCase()
    : undefined;

  let address = "";
  if (property) {
    address = formatAddress(property);
  }

  let propertyType = "";
  if (property?.typeId) {
    propertyType = (
      getPropertyTypeString({ typeId: property.typeId }) || ""
    ).toLowerCase();
  }

  let financialAdmin = "Rentio";
  if (property) {
    const members = property.members.filter(member =>
      member.roles.includes(EPropertyMemberTypes.FinancialManager),
    );

    if (members.length > 0) {
      financialAdmin = getName(members[0].account);
    }
  }

  return {
    financial_admin: financialAdmin,
    optional_period: optionalPeriod ? ` ${optionalPeriod}` : "",
    propertyType,
    address,
    paymentType,
    sepaBankDaysOffset: `${paymentRequestUtils.sepaDeductionDaysFromNow(
      paymentRequest,
    )}`,
  };
};

// INFO: the tenant dashboard has no page for a single contract; the Contracts
// tab of its property is where a tenant sees it.
export const getContractRoute = ({
  propertyId,
}: Pick<IContract, "propertyId">) => `/properties/${propertyId}/contracts`;

export const getContractDurationLabel = ({
  propertyAddress,
  startDate,
  stopDate,
}: Pick<IContract, "propertyAddress" | "startDate" | "stopDate">) => {
  const end = stopDate
    ? formatDate(stopDate)
    : getLocalizedText("contract.indefinite");
  const duration = `(${formatDate(startDate)} - ${end})`;

  return propertyAddress?.full
    ? `${propertyAddress.full} ${duration}`
    : duration;
};

// Two contracts on the same property can share address and period (a flat and
// a garage, or a second contract next to the first one), so the duration label
// alone does not tell them apart. The rent does, and it is shown on every row so
// the rows read the same whether or not a duplicate exists.
export const getContractRentLabel = ({
  currentPrice,
}: Pick<IContract, "currentPrice">) =>
  currentPrice != null
    ? getLocalizedText("system.contract.rent", {
        amount: formatCurrency(currentPrice),
      })
    : undefined;

const NO_BREAK_SPACE = "\u00A0";

// The rent part ("· € 500,00 rent") stays on one line: on a narrow screen the
// label otherwise wraps right after the "€" and leaves the symbol alone at the
// end of the first line.
export const keepOnOneLine = (text: string) =>
  text.replace(/ /g, NO_BREAK_SPACE);

export const getContractLabel = (
  contract: Pick<
    IContract,
    "propertyAddress" | "startDate" | "stopDate" | "currentPrice"
  >,
) => {
  const durationLabel = getContractDurationLabel(contract);
  const rentLabel = getContractRentLabel(contract);

  return rentLabel
    ? `${durationLabel}${NO_BREAK_SPACE}· ${keepOnOneLine(rentLabel)}`
    : durationLabel;
};

export enum EDirectDebitPresence {
  Present = "present",
  Absent = "absent",
  Unknown = "unknown",
}

interface IDirectDebitPresenceParams {
  contractGroupCount: number;
  bankAccountCount: number;
  isResolvingContractGroups: boolean;
  isResolvingBankAccounts: boolean;
}

// Data already in the store counts straight away; Absent needs both sources
// to have answered, so the payments page never calls a tenant empty while the
// direct debit data is still on its way.
export const getDirectDebitPresence = ({
  contractGroupCount,
  bankAccountCount,
  isResolvingContractGroups,
  isResolvingBankAccounts,
}: IDirectDebitPresenceParams): EDirectDebitPresence => {
  if (contractGroupCount > 0 || bankAccountCount > 0) {
    return EDirectDebitPresence.Present;
  }

  if (isResolvingContractGroups || isResolvingBankAccounts) {
    return EDirectDebitPresence.Unknown;
  }

  return EDirectDebitPresence.Absent;
};

// The back link goes straight into navigate(). A protocol-relative value
// ("//evil.com", or "/\evil.com", which browsers read the same way) or a full
// URL ("https://evil.com") would leave the app, so only a path that starts with
// a single "/" is accepted. Browsers also drop tab and newline characters from
// a URL, so "/\t/evil.com" would turn into "//evil.com": those are rejected too.
const URL_STRIPPED_CHARACTERS = /[\t\n\r]/;

export const isInAppPath = (path: string) =>
  path.startsWith("/") &&
  path[1] !== "/" &&
  path[1] !== "\\" &&
  !URL_STRIPPED_CHARACTERS.test(path);

interface IAddBankAccountSuccessPathParams {
  referrer?: string;
  ibanId: number;
}

// After a new bank account the tenant goes back to where they came from, with
// the new account preselected (?ibanId=). Only an in-app referrer is followed.
export const getAddBankAccountSuccessPath = ({
  referrer,
  ibanId,
}: IAddBankAccountSuccessPathParams) => {
  const basePath =
    referrer && isInAppPath(referrer) ? referrer : PAYMENTS_ROUTE;
  const { url, query } = queryString.parseUrl(basePath);

  return queryString.stringifyUrl({
    url,
    query: { ...query, ibanId: `${ibanId}` },
  });
};

interface IAddBankAccountBackLinkParams {
  paymentRequestId?: number;
  referrer?: string;
}

export const getAddBankAccountBackLink = ({
  paymentRequestId,
  referrer,
}: IAddBankAccountBackLinkParams) => {
  if (paymentRequestId) {
    return {
      path: `/payment/${paymentRequestId}/payment-method`,
      labelKey: "system.back",
    };
  }

  if (!referrer || !isInAppPath(referrer)) {
    return { path: PAYMENTS_ROUTE, labelKey: "payments.overview.title" };
  }

  const { url } = queryString.parseUrl(referrer);

  if (url === DIRECT_DEBITS_ROUTE) {
    return {
      path: referrer,
      labelKey: "tenant.payments.direct_debit.manage_title",
    };
  }

  if (url === PAYMENTS_ROUTE) {
    return { path: referrer, labelKey: "payments.overview.title" };
  }

  return { path: referrer, labelKey: "system.back" };
};
